import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { padColumns } from 'utils/pad-columns';

import type { WithdrawalRequests } from 'api';
import { useGetDisbursementWithdrawalRequests } from 'api';

import { selectOrganisationId } from 'reduxLocal/slices/authSlice';

import { useVirtualAccounts } from 'hooks/context';

import { apiParams } from 'components/pages/Withdrawals/apiAdapter';
import { getColumns } from 'components/pages/Withdrawals/columns';
import { WithdrawalDetails } from 'components/pages/Withdrawals/Details/WithdrawalDetails';
import { useLocalState } from 'components/pages/Withdrawals/localState';

import { WITHDRAWALS_SEARCH_RESULT_TEST_ID } from './constants';

import { SearchResults } from '../SearchResults';

type Props = {
  onSuccess: (withdrawal: WithdrawalRequests | undefined) => void;
  searchParams: any;
};

export const WithdrawalsSearchResults = ({
  searchParams,
  onSuccess
}: Props) => {
  const organisationId = useSelector(selectOrganisationId);
  if (!organisationId) throw new Error('Missing organisation id');

  const [selectedRowData, setSelectedRowData] = useState<
    WithdrawalRequests | undefined
  >(undefined);

  const { state } = useLocalState({
    filters: { reference: searchParams.unique_identifier }
  });

  const {
    data: { data: withdrawals = [] } = {},
    isFetching,
    error,
    isSuccess
  } = useGetDisbursementWithdrawalRequests(
    organisationId,
    apiParams({
      organisationId: organisationId.toString(),
      filters: {
        reference: searchParams.unique_identifier
      },
      sorting: state.sorting,
      cursorAfter: state.cursorAfter,
      cursorBefore: state.cursorBefore,
      includeCursor: state.includeCursor
    })
  );

  useEffect(() => {
    if (!isFetching && isSuccess) {
      onSuccess(withdrawals?.[0]);
    }
  }, [isFetching, isSuccess, onSuccess, withdrawals]);

  useEffect(() => {
    if (withdrawals) {
      setSelectedRowData(withdrawals?.[0]);
    }
  }, [withdrawals]);

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const columns = padColumns(getColumns(findAccountByAccountNumber));

  const handleRowClick = (record: WithdrawalRequests) => {
    setSelectedRowData((selected) =>
      selected !== record ? record : undefined
    );
  };

  if (withdrawals.length > 0 && !error && isSuccess) {
    return (
      <SearchResults
        idKey="id"
        title="Withdrawals"
        data={withdrawals}
        e2eId={WITHDRAWALS_SEARCH_RESULT_TEST_ID}
        isFetching={isFetching}
        columnsDefinition={columns}
        onRowClick={handleRowClick}
        detailComponent={
          selectedRowData && (
            <WithdrawalDetails
              onClose={() => setSelectedRowData(undefined)}
              selectedRowData={selectedRowData}
            />
          )
        }
        isSelected={(record) => record.id === selectedRowData?.id}
      />
    );
  }
};
